import React, { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import IconCollection from "../../IconCollection/IconCollection";

const MANAGER_API = process.env.REACT_APP_MANAGER_API;

/**
 * Self-contained player add form. Each team gets its OWN instance
 * with fully isolated state — no cross-contamination between teams.
 *
 * Props:
 *  - eventData: full event object from StatGrit
 *  - teamId: numeric team ID (event_team_a_id or event_team_b_id)
 *  - teamName: display name (team_a_short_name or team_b_short_name)
 *  - existingRosters: array of current rosters for THIS team
 *  - token: auth token string
 *  - onSuccess: callback after successful add (triggers data reload)
 *  - onCancel: callback to hide the form
 */
const PlayerAddForm = ({
  eventData,
  teamId,
  teamName,
  existingRosters,
  token,
  onSuccess,
  onCancel,
}) => {
  // --- fully isolated form state ---
  const [jersey, setJersey] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [jerseyError, setJerseyError] = useState("");
  const jerseyRef = useRef(null);
  const searchTimeout = useRef(null);

  // auto-focus jersey field on mount
  useEffect(() => {
    jerseyRef.current?.focus();
  }, []);

  // --- reset ---
  const resetForm = useCallback(() => {
    setJersey("");
    setSearchQuery("");
    setSearchResults([]);
    setSelectedUser(null);
    setJerseyError("");
  }, []);

  // --- user search (Manager API, debounced) ---
  const handleSearch = useCallback(
    (q) => {
      setSearchQuery(q);
      if (searchTimeout.current) clearTimeout(searchTimeout.current);

      if (q.length < 2) {
        setSearchResults([]);
        return;
      }

      searchTimeout.current = setTimeout(async () => {
        setSearching(true);
        try {
          const res = await axios.get(
            `${MANAGER_API}/api/v1/users/search`,
            {
              params: { q, limit: 10 },
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const users = res.data?.data || res.data || [];
          setSearchResults(users);
        } catch {
          setSearchResults([]);
        } finally {
          setSearching(false);
        }
      }, 300);
    },
    [token]
  );

  // --- select user from dropdown ---
  const handleSelectUser = useCallback((user) => {
    setSelectedUser(user);
    setSearchQuery("");
    setSearchResults([]);
  }, []);

  // --- clear selected user ---
  const handleClearUser = useCallback(() => {
    setSelectedUser(null);
    setSearchQuery("");
    setSearchResults([]);
  }, []);

  // --- submit ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // validate jersey
    if (!jersey.trim()) {
      setJerseyError("Jersey number is required");
      jerseyRef.current?.focus();
      return;
    }
    if (!/^\d{1,2}$/.test(jersey.trim())) {
      setJerseyError("Enter a number 0-99");
      jerseyRef.current?.focus();
      return;
    }

    const padded = jersey.trim().padStart(2, "0");

    // duplicate check against existing rosters
    const isDuplicate = existingRosters?.some(
      (r) => String(r.jersey_number).padStart(2, "0") === padded
    );
    if (isDuplicate) {
      setJerseyError(`Jersey #${padded} is already taken on ${teamName}`);
      jerseyRef.current?.focus();
      return;
    }

    setJerseyError("");
    setSubmitting(true);

    try {
      const res = await axios.post(
        `${MANAGER_API}/api/v1/events/rosters`,
        {
          event_group_id: eventData.event_group_id,
          team_id: teamId,
          customer_id: selectedUser?.id || null,
          jersey_number: padded,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (
        res.data?.status === "0" ||
        res.data?.message === "success" ||
        res.status === 200
      ) {
        toast.success(
          `Added ${selectedUser?.name || `#${padded}`} to ${teamName}`
        );
        resetForm();
        onSuccess?.();
      }
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Failed to add player";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const hasInput = jersey.trim() || selectedUser;

  return (
    <form onSubmit={handleSubmit} className="pb-4">
      {/* Search dropdown — rendered ABOVE the form, opens upward */}
      {searchResults.length > 0 && !selectedUser && (
        <div className="relative mb-1">
          <div className="bg-white border border-[#E8E8E8] rounded shadow-lg max-h-[220px] overflow-y-auto">
            {searchResults.map((user) => (
              <button
                key={user.id}
                type="button"
                className="w-full text-left px-3 py-2.5 hover:bg-blue-50 flex items-center gap-3 border-b border-[#f0f0f0] last:border-0 transition-colors"
                onClick={() => handleSelectUser(user)}
              >
                <span className="text-xs text-amber-600 font-mono min-w-[60px] font-semibold">
                  {user.code || user.username || ""}
                </span>
                <div className="flex-1 min-w-0">
                  <span className="font-montserrat text-sm block truncate">
                    {user.name}
                  </span>
                  {user.email && (
                    <span className="text-[11px] text-gray-400 block truncate">
                      {user.email}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center w-full border border-[#E8E8E8] rounded bg-white">
        {/* Jersey number */}
        <div className="min-w-[80px] max-w-[80px] h-[48px] flex items-center justify-center border-r border-[#E8E8E8] px-2">
          <input
            ref={jerseyRef}
            type="text"
            inputMode="numeric"
            maxLength={2}
            className="bg-transparent outline-none w-full text-center font-montserrat font-bold text-lg"
            placeholder="#"
            value={jersey}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, "").slice(0, 2);
              setJersey(v);
              setJerseyError("");
            }}
            disabled={submitting}
          />
        </div>

        {/* Player search / selected display */}
        <div className="flex-1 h-[48px] flex items-center px-3 min-w-0">
          {selectedUser ? (
            <div className="flex items-center gap-2 w-full min-w-0">
              <span className="text-xs text-amber-600 font-mono font-semibold flex-shrink-0">
                {selectedUser.code || selectedUser.username || ""}
              </span>
              <span className="font-montserrat text-sm truncate flex-1">
                {selectedUser.name}
              </span>
              <button
                type="button"
                onClick={handleClearUser}
                className="text-gray-400 hover:text-red-500 ml-1 flex-shrink-0"
                disabled={submitting}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </div>
          ) : (
            <div className="flex items-center w-full">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-300 mr-2 flex-shrink-0">
                <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
              </svg>
              <input
                type="text"
                className="bg-transparent outline-none w-full font-montserrat text-sm"
                placeholder="Search player by name or code..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                disabled={submitting}
              />
              {searching && (
                <div className="ml-2 flex-shrink-0">
                  <div className="w-4 h-4 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1 px-2 border-l border-[#E8E8E8]">
          {hasInput && (
            <button
              type="button"
              onClick={() => { resetForm(); onCancel?.(); }}
              className="p-1 text-gray-400 hover:text-red-500"
              disabled={submitting}
              title="Cancel"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          )}
          <button
            type="submit"
            disabled={submitting || !jersey.trim()}
            className="p-1 text-primary hover:text-green-700 disabled:text-gray-300"
            title="Add player"
          >
            {submitting ? (
              <div className="w-5 h-5 border-2 border-gray-200 border-t-primary rounded-full animate-spin" />
            ) : (
              <IconCollection name="plus-icon" />
            )}
          </button>
        </div>
      </div>

      {/* Error message */}
      {jerseyError && (
        <p className="text-red-500 text-xs mt-1 pl-2">{jerseyError}</p>
      )}
    </form>
  );
};

export default PlayerAddForm;
